import { ReactNode, useCallback, useState } from 'react'

import { ChevronDown, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

import { SplitsLink } from '~/components/Link'
import { Button } from '~/components/component-library'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { SimulationButton } from '~/components/txn-display/state-change/components/SimulationButton'
import { useNavigation } from '~/hooks/useNavigation'
import { useIsMobile } from '~/utils/devices'
import { sendTrackingData } from '~/utils/tracking'

export type DetailsDropdownOption =
  | { type: 'link'; label: string; url: string }
  | {
      type: 'simulation'
      label: string
      runSimulation: () => Promise<string | undefined>
    }

export type SimulationDisplayProps = {
  runTenderlySimulation?: () => Promise<string | undefined>
  simulationUrl?: string
  tenderlyUrl?: string
}

type SecondaryDisplayButtonsProps = SimulationDisplayProps & {
  isShowingCalls: boolean
  onToggleCalls: (event: React.MouseEvent) => void
  hasDecodedCalls: boolean
  detailsOptions?: DetailsDropdownOption[]
  linkTarget?: 'tab' | 'window'
}

const SimulationDropdownItem = ({
  label,
  runSimulation,
  linkTarget = 'tab',
}: {
  label: string
  runSimulation: () => Promise<string | undefined>
  linkTarget?: 'tab' | 'window'
}) => {
  const navigate = useNavigation()
  const [isLoading, setIsLoading] = useState(false)
  const [cachedUrl, setCachedUrl] = useState<string | undefined>()

  const handleSelect = useCallback(
    async (e: Event) => {
      e.preventDefault()

      if (isLoading) return

      sendTrackingData('txn-display:simulate', { label })

      // If we already have a cached URL, just navigate
      if (cachedUrl) {
        navigate(cachedUrl, { target: linkTarget })
        return
      }

      try {
        setIsLoading(true)
        const url = await runSimulation()

        if (!url) throw new Error('No simulation URL returned')

        setCachedUrl(url)
        navigate(url, { target: linkTarget })
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        toast.error('Simulation failed', {
          description: message,
        })
      } finally {
        setIsLoading(false)
      }
    },
    [runSimulation, navigate, isLoading, cachedUrl, label, linkTarget],
  )

  return (
    <DropdownMenuItem
      onSelect={handleSelect}
      className="flex w-full cursor-pointer items-center justify-between gap-3"
    >
      {label}
      {isLoading ? (
        <LoadingSpinner size="md" />
      ) : (
        <ExternalLink className="size-3" />
      )}
    </DropdownMenuItem>
  )
}

export const SecondaryDisplayButtons = ({
  isShowingCalls,
  onToggleCalls,
  hasDecodedCalls,
  runTenderlySimulation,
  simulationUrl,
  tenderlyUrl,
  detailsOptions,
  linkTarget = 'tab',
}: SecondaryDisplayButtonsProps): ReactNode => {
  const isMobile = useIsMobile()

  const detailsButton =
    detailsOptions && detailsOptions.length > 0 ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondaryAction"
            suffix={<ChevronDown className="size-2.5" />}
          >
            Details
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={isMobile ? 'center' : 'start'}
          side={isMobile ? 'bottom' : 'left'}
          className="w-48"
        >
          {detailsOptions.map((option) =>
            option.type === 'link' ? (
              <DropdownMenuItem key={option.url} asChild>
                <SplitsLink
                  path={option.url}
                  target={linkTarget}
                  className="flex w-full cursor-pointer items-center justify-between gap-3"
                  eventName="txn-display:view-receipt"
                  eventProperties={{ url: option.url }}
                >
                  {option.label}
                  <ExternalLink className="size-3" />
                </SplitsLink>
              </DropdownMenuItem>
            ) : (
              <SimulationDropdownItem
                key={option.label}
                label={option.label}
                runSimulation={option.runSimulation}
                linkTarget={linkTarget}
              />
            ),
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : tenderlyUrl ? (
      <Button
        variant="secondaryAction"
        suffix={<ExternalLink className="size-2.5" />}
        eventName="txn-display:view-receipt"
        eventProperties={{ url: tenderlyUrl }}
        href={{
          path: tenderlyUrl,
          target: linkTarget,
        }}
      >
        Details
      </Button>
    ) : runTenderlySimulation ? (
      <SimulationButton
        runTenderlySimulation={runTenderlySimulation}
        defaultSimulationUrl={simulationUrl}
        linkTarget={linkTarget}
      />
    ) : null

  return (
    <div className="flex items-center gap-3">
      {hasDecodedCalls && (
        <Button
          variant="secondaryAction"
          className="text-secondary"
          onClick={(e) => {
            e?.preventDefault()
            e?.stopPropagation()
            onToggleCalls(e)
          }}
        >
          {isShowingCalls ? 'Hide calls' : 'Show calls'}
        </Button>
      )}
      {detailsButton}
    </div>
  )
}
